/**
 * Spec 030: pure rules behind `SectionNavLayout`, kept apart so they can be
 * tested without a browser layout engine.
 */

/** Gap between the top of the viewport and a section title after a jump. */
export const DESKTOP_SCROLL_OFFSET = 16;
/** The phone bar (48px) plus a 12px gap. */
export const MOBILE_BAR_HEIGHT = 48;
export const MOBILE_SCROLL_OFFSET = MOBILE_BAR_HEIGHT + 12;
/** A section counts as current once its top passes this far below the offset line. */
export const ACTIVE_SLACK = 24;

export function sectionIdFromHash(hash: string, sections: { id: string }[]): string | null {
  const id = decodeURIComponent((hash || '').replace(/^#/, ''));
  if (!id) return null;
  return sections.some(s => s.id === id) ? id : null;
}

/**
 * The current section: the last one whose top (relative to the viewport) has
 * reached the offset line. At the bottom of the page the last section wins even
 * if it is short.
 */
export function pickActiveSection(
  tops: { id: string; top: number }[],
  offset: number,
  atBottom: boolean,
): string | null {
  if (tops.length === 0) return null;
  if (atBottom) return tops[tops.length - 1].id;
  let current = tops[0].id;
  for (const t of tops) {
    if (t.top <= offset + ACTIVE_SLACK) current = t.id;
  }
  return current;
}

/**
 * Sticky done by hand: the app shell sets `overflow-x: hidden` on its wrappers,
 * which stops `position: sticky` from sticking, so the rail and the phone bar
 * are `position: fixed` and follow their container's box.
 */
export function followBox(
  container: { top: number; bottom: number },
  viewportHeight: number,
  slotTop: number,
  barHeight: number,
): { railTop: number; railHeight: number; barTop: number } {
  const railTop = Math.max(0, container.top);
  const railBottom = Math.min(viewportHeight, container.bottom);
  const barTop = Math.min(Math.max(0, slotTop), container.bottom - barHeight);
  return { railTop, railHeight: Math.max(0, railBottom - railTop), barTop };
}

/**
 * Space to add under the last card so it can still scroll up to the offset
 * line (FR-013). `docHeight` excludes the spacer itself.
 */
export function bottomSpacerHeight(args: {
  lastSectionTopInDoc: number;
  offset: number;
  docHeight: number;
  viewportHeight: number;
}): number {
  const neededScroll = args.lastSectionTopInDoc - args.offset;
  const maxScroll = args.docHeight - args.viewportHeight;
  return Math.max(0, Math.ceil(neededScroll - maxScroll));
}

export function readExpanded(storageKey: string): boolean {
  try {
    return window.localStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
}

export function writeExpanded(storageKey: string, expanded: boolean): void {
  try {
    window.localStorage.setItem(storageKey, expanded ? '1' : '0');
  } catch {
    // Private windows and blocked storage: the rail just starts collapsed.
  }
}

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** True from Tailwind `md` (768px) up, where the rail replaces the phone bar. */
export function isRailLayout(): boolean {
  try {
    return window.matchMedia('(min-width: 768px)').matches;
  } catch {
    return window.innerWidth >= 768;
  }
}

/* ---------------------------------------------------------- scrolling */

function isDocumentScroller(el: Element): boolean {
  return el === document.scrollingElement || el === document.documentElement;
}

/**
 * The element that scrolls the page. In this app it is `<body>`, not the
 * window: `index.css` gives `html`, `body` and `#root` `height: 100%`, and the
 * shell's `overflow-x: hidden` turns body into the scroll container.
 */
export function scrollContainerOf(el: Element | null): HTMLElement {
  let node = el?.parentElement ?? null;
  while (node && node !== document.documentElement) {
    const oy = window.getComputedStyle(node).overflowY;
    if (
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/** Viewport top of the scroller's visible area (0 for the page itself). */
function viewTop(scroller: HTMLElement): number {
  return isDocumentScroller(scroller) ? 0 : scroller.getBoundingClientRect().top;
}

/** An element's offset from the top of the scroller's content. */
export function topInScroller(el: Element, scroller: HTMLElement): number {
  return el.getBoundingClientRect().top - viewTop(scroller) + scroller.scrollTop;
}

/** An element's top inside the scroller's visible area. */
export function topInView(el: Element, scroller: HTMLElement): number {
  return el.getBoundingClientRect().top - viewTop(scroller);
}

export function scrollerViewHeight(scroller: HTMLElement): number {
  return isDocumentScroller(scroller) ? window.innerHeight : scroller.clientHeight;
}

export function scrollScrollerTo(
  scroller: HTMLElement,
  top: number,
  behavior: 'auto' | 'smooth',
): void {
  if (isDocumentScroller(scroller)) {
    window.scrollTo({ top, behavior });
  } else if (typeof scroller.scrollTo === 'function') {
    scroller.scrollTo({ top, behavior });
  } else {
    scroller.scrollTop = top;
  }
}
